import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Pencil, Trash2, ArrowLeft, Save, X } from "lucide-react";
import { Logo } from "../../components/Logo";
import { API, authHeaders, formatError } from "../../lib/adminApi";
import { TEMPLATE_TAGS } from "../../lib/templateRender";

export default function AdminTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const bodyRef = useRef(null);

  const load = async () => {
    try { const r = await axios.get(`${API}/admin/message-templates`, { headers: authHeaders() }); setTemplates(r.data || []); }
    catch (e) { setErr(formatError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setErr("");
    try {
      if (editing.id) await axios.put(`${API}/admin/message-templates/${editing.id}`, editing, { headers: authHeaders() });
      else await axios.post(`${API}/admin/message-templates`, editing, { headers: authHeaders() });
      setEditing(null); load();
    } catch (e) { setErr(formatError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await axios.delete(`${API}/admin/message-templates/${id}`, { headers: authHeaders() });
    load();
  };

  const insertTag = (tag) => {
    const el = bodyRef.current;
    const body = editing.body || "";
    if (!el) { setEditing({ ...editing, body: body + tag }); return; }
    const s = el.selectionStart ?? body.length;
    const e = el.selectionEnd ?? body.length;
    const next = body.slice(0, s) + tag + body.slice(e);
    setEditing({ ...editing, body: next });
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + tag.length, s + tag.length); });
  };

  return (
    <div className="min-h-screen bg-white/60">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3"><Logo /> <span className="font-heading font-bold text-lg">Message Templates</span></div>
        <button onClick={() => navigate("/admin/dashboard")} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#FF5E1A]"><ArrowLeft className="w-4 h-4" /> Dashboard</button>
      </header>
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-4">
          <h1 className="font-heading text-2xl font-bold">Templates</h1>
          <button onClick={() => setEditing({ title: "", body: "" })} data-testid="template-add-btn"
            className="inline-flex items-center gap-1 rounded-full bg-[#FF5E1A] text-white px-4 py-2 text-sm font-semibold"><Plus className="w-4 h-4" /> New template</button>
        </div>
        <p className="text-xs text-slate-500 mb-4">Use tags like {"{CPName}"} or {"{Link}"} — they get replaced with the CP's details and their personal onboarding link when the message is sent.</p>

        {loading ? <div className="text-slate-500">Loading…</div> : (
          <div className="flex flex-col gap-3" data-testid="template-list">
            {templates.map((t) => (
              <div key={t.id} className="glass-strong rounded-2xl p-4" data-testid={`template-row-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-bold">{t.title}</div>
                    <div className="text-xs text-slate-500 mt-1 whitespace-pre-wrap line-clamp-3">{t.body}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setEditing({ ...t })} data-testid={`template-edit-${t.id}`} className="p-2 rounded-lg text-slate-500 hover:text-[#FF5E1A] hover:bg-white"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(t.id)} data-testid={`template-delete-${t.id}`} className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-white"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
            {templates.length === 0 && <div className="glass-strong rounded-2xl p-6 text-center text-slate-500">No templates yet. Create your first one.</div>}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center px-4 py-6 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl w-full max-w-xl p-6 shadow-2xl my-6" onClick={(e) => e.stopPropagation()} data-testid="template-edit-modal">
            <h3 className="font-heading font-bold text-lg mb-4">{editing.id ? "Edit template" : "New template"}</h3>
            <div className="flex flex-col gap-3">
              <input type="text" placeholder="Template name (e.g. First WhatsApp message)" value={editing.title || ""}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                data-testid="template-title" className="px-3 py-2 rounded-xl glass-input text-sm" />
              <div className="flex flex-wrap gap-1.5" data-testid="template-tags">
                {TEMPLATE_TAGS.map((t) => (
                  <button key={t.tag} type="button" onClick={() => insertTag(t.tag)} data-testid={`template-tag-${t.label.replace(/\s+/g, "-")}`}
                    className="rounded-full bg-[#FFF3ED] text-[#E04F00] border border-[#FFD9C7] px-2.5 py-1 text-[11px] font-semibold hover:bg-[#FF5E1A] hover:text-white transition-colors">
                    {t.tag}
                  </button>
                ))}
              </div>
              <textarea ref={bodyRef} placeholder={"Namaste {CPName} ji,\n\nAapka personal onboarding link taiyaar hai: {Link}\n\n— {YourName}, ChannelPartner.Network"}
                value={editing.body || ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                data-testid="template-body" className="px-3 py-2 rounded-xl glass-input text-sm min-h-[160px]" />
              {err && <div className="text-xs text-red-600">{err}</div>}
              <div className="flex gap-2 justify-end">
                <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm rounded-xl bg-white border border-slate-300 inline-flex items-center gap-1"><X className="w-4 h-4" /> Cancel</button>
                <button onClick={save} data-testid="template-save" className="px-4 py-2 text-sm rounded-xl bg-[#FF5E1A] text-white font-semibold inline-flex items-center gap-1"><Save className="w-4 h-4" /> Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
